<script setup lang="ts">
/**
 * 规格选择弹层（份量 / 加料 / 辣度）。
 *
 * 规则完全跟着后端 `product_variants.select_type` 走，前端**不自己发明规则**：
 *   - SINGLE：同组只能选一个（默认选中后端标的 isDefault，没有就选第一个）
 *   - MULTIPLE：同组可以多选，也可以都不选
 *
 * 价格这里会算一下（基础价 + 已选规格加价），用整数分、用 shared 里那份
 * 经过测试的加法。但它只是给顾客看的，并且会作为 expectedUnitPriceCents
 * 发给服务端；服务端会重新算一遍，对不上就让顾客重新确认。
 */
import { computed, ref, watch } from 'vue';
import { formatYuan, sumCents } from '@jinhua/shared';
import type { MenuProduct } from '../api/types';

const props = defineProps<{ product: MenuProduct | null }>();
const emit = defineEmits<{
  (e: 'close'): void;
  (
    e: 'confirm',
    payload: {
      variantIds: string[];
      variantText: string | null;
      unitPriceCents: number;
      quantity: number;
      remark: string | null;
    },
  ): void;
}>();

/** 分组：保持后端给的顺序 */
const groups = computed(() => {
  const map = new Map<string, MenuProduct['variants']>();
  for (const variant of props.product?.variants ?? []) {
    const list = map.get(variant.groupName) ?? [];
    list.push(variant);
    map.set(variant.groupName, list);
  }
  return Array.from(map.entries()).map(([groupName, variants]) => ({ groupName, variants }));
});

const selected = ref<string[]>([]);
const quantity = ref(1);
const remark = ref('');

/** 每次打开都重置成后端给的默认选项，不沿用上一位顾客的选择 */
watch(
  () => props.product?.id,
  () => {
    quantity.value = 1;
    remark.value = '';
    const defaults: string[] = [];
    for (const group of groups.value) {
      if (group.variants[0]?.selectType === 'SINGLE') {
        const preset = group.variants.find((v) => v.isDefault) ?? group.variants[0];
        if (preset) defaults.push(preset.id);
      }
    }
    selected.value = defaults;
  },
  { immediate: true },
);

function isSelected(id: string): boolean {
  return selected.value.includes(id);
}

function toggle(variantId: string, selectType: string): void {
  if (selectType === 'SINGLE') {
    // 单选：点新的就替换旧的（同一组内）
    const group = groups.value.find((g) => g.variants.some((v) => v.id === variantId));
    const groupIds = new Set(group?.variants.map((v) => v.id) ?? []);
    selected.value = [...selected.value.filter((id) => !groupIds.has(id)), variantId];
    return;
  }
  selected.value = isSelected(variantId)
    ? selected.value.filter((id) => id !== variantId)
    : [...selected.value, variantId];
}

/** 已选规格的中文描述，例如「大份 / 加牛肉」 */
const variantText = computed(() => {
  const names: string[] = [];
  for (const group of groups.value) {
    for (const variant of group.variants) {
      if (isSelected(variant.id)) names.push(variant.name);
    }
  }
  return names.length > 0 ? names.join(' / ') : null;
});

const unitPriceCents = computed(() => {
  const deltas: number[] = [];
  for (const group of groups.value) {
    for (const variant of group.variants) {
      if (isSelected(variant.id)) deltas.push(variant.priceDeltaCents);
    }
  }
  return sumCents([props.product?.priceCents ?? 0, ...deltas]);
});

const unitPriceText = computed(() => formatYuan(unitPriceCents.value));

function confirm(): void {
  emit('confirm', {
    variantIds: [...selected.value],
    variantText: variantText.value,
    unitPriceCents: unitPriceCents.value,
    quantity: quantity.value,
    remark: remark.value.trim() === '' ? null : remark.value.trim(),
  });
}
</script>

<template>
  <div v-if="product" class="overlay" @click.self="emit('close')">
    <div class="dialog">
      <div class="dialog__title">{{ product.name }}</div>

      <div class="dialog__body variant-body">
        <section v-for="group in groups" :key="group.groupName" class="group">
          <div class="group__name">
            {{ group.groupName }}
            <span class="tag">{{ group.variants[0]?.selectType === 'SINGLE' ? '选一个' : '可多选' }}</span>
          </div>
          <div class="group__options">
            <button
              v-for="variant in group.variants"
              :key="variant.id"
              class="option"
              :class="{ 'option--on': isSelected(variant.id) }"
              @click="toggle(variant.id, variant.selectType)"
            >
              <span>{{ variant.name }}</span>
              <span v-if="variant.priceDeltaCents !== 0" class="option__delta">
                {{ variant.priceDeltaCents > 0 ? '+' : '-' }}{{ variant.priceDeltaText.replace('-', '') }}
              </span>
            </button>
          </div>
        </section>

        <section class="group">
          <div class="group__name">备注 <span class="tag">可留空</span></div>
          <input
            v-model="remark"
            class="remark"
            type="text"
            maxlength="60"
            placeholder="例如：不要香菜、少放辣"
          />
        </section>
      </div>

      <div class="dialog__footer">
        <div class="stepper">
          <button class="stepper__btn" @click="quantity = Math.max(1, quantity - 1)">−</button>
          <span class="stepper__value">{{ quantity }}</span>
          <button class="stepper__btn" @click="quantity += 1">＋</button>
        </div>
        <div class="spacer" />
        <button class="btn btn--ghost" @click="emit('close')">取消</button>
        <button class="btn btn--primary" @click="confirm">加入 ¥{{ unitPriceText }}</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.variant-body {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.group__name {
  font-size: 19px;
  font-weight: 700;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.group__options {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.option {
  min-height: var(--touch);
  padding: 0 22px;
  border-radius: 14px;
  border: 2px solid var(--line);
  background: var(--panel-2);
  font-size: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.option--on {
  border-color: var(--brand);
  background: rgba(217, 43, 43, 0.18);
  color: #ffd7d7;
}

.option__delta {
  font-size: 15px;
  color: var(--muted);
}

.option--on .option__delta {
  color: #ffbcbc;
}

.remark {
  width: 100%;
  min-height: var(--touch);
  border-radius: 14px;
  border: 2px solid var(--line);
  background: var(--panel-2);
  color: var(--text);
  font-size: 20px;
  padding: 0 18px;
  /* 触屏输入要用系统键盘，别拦着 */
  user-select: text;
}

.stepper {
  display: flex;
  align-items: center;
  gap: 12px;
}

.stepper__btn {
  width: var(--touch);
  height: var(--touch);
  border-radius: 14px;
  background: var(--panel-2);
  border: 2px solid var(--line);
  font-size: 30px;
  font-weight: 700;
}

.stepper__value {
  min-width: 56px;
  text-align: center;
  font-size: 28px;
  font-weight: 800;
}

@media (max-width: 860px) {
  .group__name {
    font-size: 16px;
    margin-bottom: 8px;
  }

  .group__options {
    gap: 8px;
  }

  .option {
    min-height: 52px;
    padding: 0 14px;
    font-size: 16px;
    border-radius: 12px;
  }

  .remark {
    min-height: 52px;
    font-size: 16px;
  }

  .stepper__btn {
    width: 56px;
    height: 56px;
    font-size: 24px;
  }

  .stepper__value {
    min-width: 40px;
    font-size: 22px;
  }
}

</style>
